import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  InteractionStatus,
  SilentRequest,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { useDispatch, useSelector } from "react-redux";
import { loginRequest } from "~/packages/msalInstance";
import api from "~/api";
import { LinearProgress } from "@material-ui/core";
import { toast } from "react-toastify";
import { loggedIn, setUserData } from "~/store/auth/actions";

interface Props {
  children?: React.ReactNode;
}

const AuthProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const { accounts, instance, inProgress } = useMsal();
  const account: AccountInfo | undefined = accounts[0];
  const [loading, setLoading] = useState(true);
  const connectPromiseRef = useRef<Promise<void> | null>(null);
  const backendAccessToken = useSelector(
    (s: ReduxStore.State) => s.auth.authData?.accessToken
  );

  const isInteractionRequiredError = (e: unknown) => {
    if (e instanceof InteractionRequiredAuthError) return true;
    const errorCode =
      typeof e === "object" && e !== null && "errorCode" in e
        ? String((e as any).errorCode)
        : "";

    return [
      "interaction_required",
      "login_required",
      "consent_required",
      // common iframe/silent failures after idle
      "monitor_window_timeout",
      "block_iframe_reload",
    ].includes(errorCode);
  };

  const acquireTokens =
    useCallback(async (): Promise<AuthenticationResult | null> => {
      if (!account || inProgress !== InteractionStatus.None) return null;
      try {
        return await instance.acquireTokenSilent({
          ...(loginRequest as SilentRequest),
          account,
        });
      } catch (e) {
        console.warn(
          "acquireTokenSilent",
          typeof e === "object" && e !== null && "errorCode" in e
            ? (e as any).errorCode
            : e
        );
        throw e;
      }
    }, [account, inProgress, instance]);

  const connect = useCallback(async () => {
    if (!account || inProgress !== InteractionStatus.None) return;
    if (connectPromiseRef.current) return connectPromiseRef.current;

    connectPromiseRef.current = (async () => {
      let keepLoading = false;
      setLoading(true);

      try {
        const res = await acquireTokens();
        const token = res?.accessToken || res?.idToken || "";
        if (!token) {
          // no token -> interactive flow required
          keepLoading = true;
          await instance.acquireTokenRedirect(loginRequest);
          return;
        }

        const {
          data: { data: connectData },
        } = await api.auth.connect(token);
        dispatch(loggedIn(connectData));

        const {
          data: { data: userData },
        } = await api.auth.getUserData(connectData.accessToken);
        dispatch(setUserData(userData));
      } catch (e) {
        if (isInteractionRequiredError(e)) {
          keepLoading = true;
          try {
            await instance.acquireTokenRedirect(loginRequest);
          } catch (redirectErr) {
            console.error("acquireTokenRedirect failed", redirectErr);
            toast.error("Authentication error");
          }
          return;
        }

        console.error("connect error", e);
        toast.error("Authentication error");
      } finally {
        connectPromiseRef.current = null;
        if (!keepLoading) setLoading(false);
      }
    })();

    return connectPromiseRef.current;
  }, [account, acquireTokens, dispatch, inProgress, instance]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (!account) {
      setLoading(false);
      return;
    }

    if (!backendAccessToken) {
      void connect();
      return;
    }

    setLoading(false);
  }, [account?.homeAccountId, inProgress, connect, backendAccessToken]);

  if (loading) return <LinearProgress />;
  return <>{children}</>;
};

export default AuthProvider;
